'use client';

import { useMemo, useRef, useState } from 'react';

import { trackCostCalculatorUsed } from '~/lib/analytics/posthog-events';

import { SpitexInlineForm } from '../../[vertical]/[city]/_components/spitex/spitex-inline-form';
import {
  CANTONS,
  SERVICE_RATES,
  computeCost,
  formatCHF,
  type CantonCode,
  type ServiceType,
} from './kvg-rates';

interface CostCalculatorProps {
  locale: string;
  verticalSlug: string;
  citySlug: string;
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  partialSaveAction?: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
}

const SERVICE_OPTIONS: { value: ServiceType; label: string }[] = [
  { value: 'behandlungspflege', label: 'Behandlungspflege' },
  { value: 'grundpflege', label: 'Grundpflege' },
  { value: 'abklaerung', label: 'Abklaerung und Beratung' },
  { value: 'hauswirtschaft', label: 'Hauswirtschaft' },
];

/**
 * Map calculator serviceType values to the lead custom-field enum values
 * defined in `SpitexDemandFieldsSchema`. `abklaerung` has no direct
 * custom-field equivalent; it funnels into `behandlungspflege` because
 * the Abklaerung visit almost always precedes Behandlungspflege.
 */
const SERVICE_TO_CUSTOM_FIELD: Record<ServiceType, string> = {
  behandlungspflege: 'behandlungspflege',
  grundpflege: 'behandlungspflege',
  abklaerung: 'behandlungspflege',
  hauswirtschaft: 'hauswirtschaft',
};

/**
 * Interactive Spitex KVG cost calculator + inline lead form.
 *
 * Inputs (top): service type, weekly hours (slider), canton, franchise toggle.
 * Output (middle): CHF/month breakdown — Spitex total, KVG, canton, out-of-pocket.
 * Lead form (bottom): re-uses SpitexInlineForm from the city landing pages so
 * lead capture flows through the same server action and pipeline. Calculator
 * state is forwarded as `initialCustomFields` so the pre-fill on step 2 shows
 * the user's selections.
 */
export function CostCalculator({
  locale,
  verticalSlug,
  citySlug,
  submitAction,
  partialSaveAction,
}: CostCalculatorProps) {
  const [serviceType, setServiceType] =
    useState<ServiceType>('behandlungspflege');
  const [weeklyHours, setWeeklyHours] = useState<number>(5);
  const [canton, setCanton] = useState<CantonCode>('ZH');
  const [franchiseFulfilled, setFranchiseFulfilled] = useState<boolean>(true);
  const hasFiredEvent = useRef(false);

  const fireOnce = () => {
    if (!hasFiredEvent.current) {
      hasFiredEvent.current = true;
      trackCostCalculatorUsed(canton, serviceType, weeklyHours, verticalSlug);
    }
  };

  const breakdown = useMemo(
    () =>
      computeCost({
        serviceType,
        weeklyHours,
        canton,
        franchiseFulfilled,
      }),
    [serviceType, weeklyHours, canton, franchiseFulfilled],
  );

  const initialCustomFields = useMemo(
    () => ({
      serviceType: SERVICE_TO_CUSTOM_FIELD[serviceType],
      canton,
      // Non-schema fields are ignored by the server Zod parser, but we still
      // stash the weekly-hours estimate inside `careDescription` so the sales
      // team has the calculator context when they pick up the lead.
      careDescription: `Kostenrechner: ${SERVICE_RATES[serviceType].label}, ${weeklyHours}h/Woche, Kanton ${canton}. Geschaetzter Eigenanteil: ${formatCHF(breakdown.monthlyOutOfPocket)}/Monat.`,
    }),
    [serviceType, canton, weeklyHours, breakdown.monthlyOutOfPocket],
  );

  return (
    <>
      <section
        id="calculator"
        className="bg-muted/40 w-full px-4 py-16 sm:py-20"
      >
        <div className="mx-auto max-w-4xl">
          <h1 className="text-foreground text-center text-3xl font-bold sm:text-4xl">
            Spitex Kostenrechner Schweiz 2026
          </h1>
          <p className="text-muted-foreground mx-auto mt-3 max-w-2xl text-center text-base sm:text-lg">
            Berechnen Sie Ihren monatlichen Eigenanteil fuer Spitex-Pflege zu
            Hause. Transparent nach KVG-Tarif und kantonaler Regelung.
          </p>

          <div className="bg-card mt-10 rounded-2xl border p-6 sm:p-8">
            {/* Inputs */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label
                  htmlFor="kr-service"
                  className="text-foreground block text-sm font-medium"
                >
                  Pflegeart
                </label>
                <select
                  id="kr-service"
                  value={serviceType}
                  onChange={(e) => {
                    setServiceType(e.target.value as ServiceType);
                    fireOnce();
                  }}
                  className="bg-background text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                >
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-muted-foreground mt-2 text-xs">
                  {SERVICE_RATES[serviceType].description}
                </p>
              </div>

              <div>
                <label
                  htmlFor="kr-canton"
                  className="text-foreground block text-sm font-medium"
                >
                  Kanton
                </label>
                <select
                  id="kr-canton"
                  value={canton}
                  onChange={(e) => {
                    setCanton(e.target.value as CantonCode);
                    fireOnce();
                  }}
                  className="bg-background text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                >
                  {CANTONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label} ({c.code})
                    </option>
                  ))}
                </select>
                <p className="text-muted-foreground mt-2 text-xs">
                  Kantonsbeitrag gemaess Art. 25a KVG. Variiert je nach
                  Wohngemeinde.
                </p>
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="kr-hours"
                  className="text-foreground flex items-center justify-between text-sm font-medium"
                >
                  <span>Stunden pro Woche</span>
                  <span className="text-foreground text-base font-semibold">
                    {weeklyHours}h
                  </span>
                </label>
                <input
                  id="kr-hours"
                  type="range"
                  min={1}
                  max={40}
                  step={1}
                  value={weeklyHours}
                  onChange={(e) => {
                    setWeeklyHours(parseInt(e.target.value, 10));
                    fireOnce();
                  }}
                  className="mt-3 w-full accent-teal-600"
                />
                <div className="text-muted-foreground mt-1 flex justify-between text-xs">
                  <span>1h</span>
                  <span>20h</span>
                  <span>40h</span>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-foreground inline-flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={franchiseFulfilled}
                    onChange={(e) => {
                      setFranchiseFulfilled(e.target.checked);
                      fireOnce();
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    KVG-Franchise bereits erfuellt (Standardfall bei laufender
                    Pflege)
                  </span>
                </label>
              </div>
            </div>

            {/* Output panel */}
            <div className="border-border mt-8 border-t pt-8">
              <div className="mb-6 text-center">
                <p className="text-muted-foreground text-sm">
                  Geschaetzte monatliche Gesamtkosten Spitex
                </p>
                <p
                  className="mt-2 text-4xl font-bold sm:text-5xl"
                  style={{ color: 'var(--landing-primary, #0D9488)' }}
                >
                  {formatCHF(breakdown.monthlyTotal)}
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  ≈ {breakdown.monthlyHours.toFixed(0)} Stunden / Monat
                </p>
              </div>

              {breakdown.isKvgCovered ? (
                <div className="space-y-3">
                  <CostBar
                    label="Krankenkasse (KVG-Beitrag)"
                    amount={breakdown.monthlyKvg}
                    total={breakdown.monthlyTotal}
                    color="var(--landing-primary, #0D9488)"
                  />
                  <CostBar
                    label="Kanton / Gemeinde (Restfinanzierung)"
                    amount={breakdown.monthlyCanton}
                    total={breakdown.monthlyTotal}
                    color="#60A5FA"
                  />
                  <div className="bg-muted/60 mt-4 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground text-sm font-medium">
                        Ihr monatlicher Eigenanteil
                      </span>
                      <span
                        className="text-2xl font-bold"
                        style={{ color: 'var(--landing-accent, #2563EB)' }}
                      >
                        {formatCHF(breakdown.monthlyOutOfPocket)}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-2 text-xs">
                      Maximal CHF 8 pro Pflegetag (Art. 25a KVG). Zuzueglich
                      Franchise und Selbstbehalt (10%) auf den KVG-Anteil, falls
                      noch nicht erfuellt.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  <p className="font-semibold">
                    Hauswirtschaft ist nicht KVG-gedeckt.
                  </p>
                  <p className="mt-1">
                    Diese Leistungen (Reinigung, Waesche, Einkaufen, Mahlzeiten)
                    werden privat in Rechnung gestellt. Einige
                    Zusatzversicherungen oder die AHV/IV-Hilflosenentschaedigung
                    uebernehmen einen Teil — wir pruefen das gerne fuer Sie.
                  </p>
                </div>
              )}
            </div>

            {/* CTA to inline form */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <a
                href="#lead-form"
                className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-teal-700 focus:ring-2 focus:ring-teal-500/40 focus:outline-none"
              >
                Diese Kostenschaetzung sichern — Spitex Frei kontaktiert Sie mit
                der genauen Offerte
              </a>
              <p className="text-muted-foreground text-xs">
                Kostenlos, unverbindlich, innert einer Stunde Rueckmeldung.
              </p>
            </div>
          </div>

          <p className="text-muted-foreground mt-6 text-center text-xs">
            Diese Berechnung ist eine unverbindliche Schaetzung. Die effektiven
            Kosten haengen vom individuellen Pflegebedarf, der Krankenkasse und
            kantonalen Regelungen ab. Quelle: KVG / KLV Tarife 2026.
          </p>
        </div>
      </section>

      <SpitexInlineForm
        side="demand"
        locale={locale}
        verticalSlug={verticalSlug}
        citySlug={citySlug}
        submitAction={submitAction}
        partialSaveAction={partialSaveAction}
        initialCustomFields={initialCustomFields}
      />
    </>
  );
}

interface CostBarProps {
  label: string;
  amount: number;
  total: number;
  color: string;
}

function CostBar({ label, amount, total, color }: CostBarProps) {
  const percent = total > 0 ? (amount / total) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">{label}</span>
        <span className="text-foreground text-sm font-semibold">
          {formatCHF(amount)}
        </span>
      </div>
      <div className="bg-muted mt-1 h-2.5 w-full overflow-hidden rounded-full">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
